import { Link } from 'react-router'
import type { LucideIcon } from 'lucide-react'
import { Home, Search, BookOpen } from 'lucide-react'

import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { Button } from '@/components/ui/button'

interface NotFoundAction {
  label: string
  to: string
  variant?: 'default' | 'outline' | 'ghost'
  icon?: LucideIcon
}

interface NotFoundPageProps {
  /**
   * Icon to display in the header circle
   */
  icon: LucideIcon
  /**
   * Main title (e.g., "Course Not Found", "Lesson Not Found")
   */
  title: string
  /**
   * Description message explaining the 404 situation
   */
  message: string
  /**
   * Action buttons to display. Defaults to standard navigation options.
   */
  actions?: NotFoundAction[]
  /**
   * Optional help text to display at the bottom
   */
  helpText?: string
}

const defaultActions: NotFoundAction[] = [
  { label: 'Go Home', to: '/', variant: 'default', icon: Home },
  { label: 'Browse Courses', to: '/courses', variant: 'outline' },
  { label: 'Browse Lessons', to: '/lessons', variant: 'outline' },
]

export function NotFoundPage({
  icon: Icon,
  title,
  message,
  actions = defaultActions,
  helpText = 'Need help? Try starting from the home page or browse our catalog of courses and lessons.',
}: NotFoundPageProps) {
  return (
    <div className='flex min-h-screen flex-col'>
      <Header />
      <main id='main-content' tabIndex={-1} className='flex flex-1 flex-col items-center justify-center px-6 py-12'>
        <div className='mx-auto max-w-md text-center'>
          <div className='mb-6 flex justify-center'>
            <div className='rounded-full bg-blue-100 p-6'>
              <Icon className='h-12 w-12 text-blue-600' />
            </div>
          </div>

          <h1 className='mb-3 text-3xl font-bold text-gray-900'>{title}</h1>

          <p className='mb-8 text-gray-600'>{message}</p>

          <div className='space-y-3'>
            {actions.map((action) => {
              const ActionIcon = action.icon
              return (
                <Button key={action.to} asChild variant={action.variant || 'default'} size='lg' className='w-full'>
                  <Link to={action.to}>
                    {ActionIcon && <ActionIcon className='mr-2 h-5 w-5' />}
                    {action.label}
                  </Link>
                </Button>
              )
            })}
          </div>

          {helpText && (
            <div className='mt-8 rounded-lg bg-gray-50 p-4'>
              <p className='text-sm text-gray-700'>
                <strong>Need help?</strong> {helpText.replace('Need help? ', '')}
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}

// Pre-configured variants for common use cases
export function CourseNotFoundPage() {
  return (
    <NotFoundPage
      icon={BookOpen}
      title='Course Not Found'
      message="We couldn't find the course you're looking for. It may have been removed, or the link might be incorrect."
      actions={[
        { label: 'Browse All Courses', to: '/courses', variant: 'default', icon: Search },
        { label: 'Explore Lessons', to: '/lessons', variant: 'outline', icon: BookOpen },
        { label: 'Go Home', to: '/', variant: 'ghost', icon: Home },
      ]}
      helpText='Try browsing our course catalog or search for lessons on a specific topic.'
    />
  )
}

export function LessonNotFoundPage() {
  return (
    <NotFoundPage
      icon={BookOpen}
      title='Lesson Not Found'
      message="We couldn't find the lesson you're looking for. It may have been removed, or the link might be incorrect."
      actions={[
        { label: 'Browse All Lessons', to: '/lessons', variant: 'default', icon: Search },
        { label: 'Explore Courses', to: '/courses', variant: 'outline', icon: BookOpen },
        { label: 'Go Home', to: '/', variant: 'ghost', icon: Home },
      ]}
      helpText='Try searching for a specific topic or browse our course catalog to find related content.'
    />
  )
}

export function GenericNotFoundPage() {
  return (
    <NotFoundPage
      icon={Search}
      title='Page Not Found'
      message="The page you're looking for doesn't exist. It may have been moved or deleted."
      helpText='Try starting from the home page or browse our catalog of courses and lessons.'
    />
  )
}
